import React, { useContext, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, ClipboardList } from "lucide-react";
import { ThemeContext } from "../../../../constant/theme";
import DatePicker from "../../../../components/DatePicker";

// URGENT removed from the selectable set per request — LOW/NORMAL/HIGH only.
// PRIORITY_COLORS/PRIORITY_LABEL_KEY below intentionally still cover URGENT
// so a legacy/API-set URGENT task (the backend's VALID_PRIORITIES still
// accepts it) still renders correctly if one is ever encountered.
const PRIORITIES = ["LOW", "NORMAL", "HIGH"];
const PRIORITY_COLORS = {
  LOW: "#22c55e",
  NORMAL: "#2e7cba",
  HIGH: "#f59e0b",
  URGENT: "#ef4444",
};
const PRIORITY_LABEL_KEY = {
  LOW: "priorityLow",
  NORMAL: "priorityNormal",
  HIGH: "priorityHigh",
  URGENT: "priorityUrgent",
};

// Create (task=null) and edit (task set — assigner-only, per plan §4.4) in
// one form. "Assign to" only renders when `canAssign` is true and this is a
// create — an existing assignment's assignee can never be changed, only its
// title/description/priority/dueDate (matches the backend's own rule that
// assignedTo is fixed at creation time).
const TaskFormModal = ({
  show,
  task,
  canAssign,
  directReports,
  styles,
  screenSize,
  t,
  language,
  onClose,
  onSubmit,
}) => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState("NORMAL");
  const [dueDate, setDueDate] = useState("");
  const [assigneeQuery, setAssigneeQuery] = useState("");
  const [assigneeCode, setAssigneeCode] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const isEdit = !!task;
  const isRTL = language === "ar";
  const { theme } = useContext(ThemeContext);

  useEffect(() => {
    if (!show) return;
    setError("");
    if (task) {
      setTitle(task.title || "");
      setDescription(task.description || "");
      setPriority(task.priority || "NORMAL");
      setDueDate(task.dueDate ? String(task.dueDate).slice(0, 10) : "");
      setAssigneeCode(task.infinityCode || "");
      setAssigneeQuery("");
    } else {
      setTitle("");
      setDescription("");
      setPriority("NORMAL");
      setDueDate("");
      setAssigneeQuery("");
      setAssigneeCode("");
    }
  }, [show, task]);

  if (!show) return null;

  const filteredReports = (directReports || []).filter((emp) =>
    emp.name.toLowerCase().includes(assigneeQuery.trim().toLowerCase())
  );
  const selectedAssignee = (directReports || []).find(
    (emp) => String(emp.infinityCode) === String(assigneeCode)
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!title.trim()) {
      setError(t.taskTitle + " " + (isRTL ? "مطلوب" : "is required"));
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit({
        title: title.trim(),
        description: description.trim() || undefined,
        priority,
        dueDate: dueDate || null,
        ...(isEdit ? {} : { assignedTo: assigneeCode || undefined }),
      });
      onClose();
    } catch (err) {
      setError(err.message || (isRTL ? "حدث خطأ" : "Something went wrong"));
    } finally {
      setSubmitting(false);
    }
  };

  // Portal to document.body — the Tasks panel's own wrapper has a
  // `backdropFilter`, which creates a CSS containing block for any
  // `position: fixed` descendant (same rule as `transform`/`perspective`).
  // Without the portal, this modal's "fixed, full-viewport" overlay was
  // actually being clipped to that panel's bounds instead of the page.
  return createPortal(
    <div style={styles.modalOverlay} onClick={onClose}>
      <div
        style={{ ...styles.modalContainer, maxWidth: 560 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={styles.modalHeader}>
          <div style={styles.modalTitleSection}>
            <div style={styles.modalIcon}>
              <ClipboardList size={screenSize.isMobile ? 22 : 26} />
            </div>
            <div>
              <h2 style={styles.modalTitle}>{isEdit ? t.editTask : t.newTask}</h2>
            </div>
          </div>
          <button style={styles.closeButton} onClick={onClose} type="button">
            <X size={screenSize.isMobile ? 20 : 24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={styles.formContainer}>
          <div style={styles.formGroup}>
            <label style={styles.formLabel}>{t.taskTitle}</label>
            <input
              style={styles.formInput}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t.taskTitlePlaceholder}
              autoFocus
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.formLabel}>{t.taskDescription}</label>
            <textarea
              style={styles.formTextarea}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t.taskDescriptionPlaceholder}
            />
          </div>

          {!isEdit && canAssign && (directReports || []).length > 0 && (
            <div style={{ ...styles.formGroup, position: "relative" }}>
              <label style={styles.formLabel}>{t.assignTo}</label>
              {selectedAssignee ? (
                <div
                  style={{
                    ...styles.formInput,
                    justifyContent: "space-between",
                    cursor: "default",
                  }}
                >
                  <span>{selectedAssignee.name}</span>
                  <button
                    type="button"
                    onClick={() => {
                      setAssigneeCode("");
                      setAssigneeQuery("");
                    }}
                    style={{ background: "none", border: "none", cursor: "pointer", color: "inherit" }}
                  >
                    <X size={16} />
                  </button>
                </div>
              ) : (
                <div style={{ position: "relative" }}>
                  <input
                    style={styles.formInput}
                    value={assigneeQuery}
                    onChange={(e) => {
                      setAssigneeQuery(e.target.value);
                      setShowSuggestions(true);
                    }}
                    onFocus={() => setShowSuggestions(true)}
                    onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                    placeholder={t.assignToPlaceholder}
                  />
                  {showSuggestions && filteredReports.length > 0 && (
                    <div
                      style={{
                        position: "absolute",
                        top: "100%",
                        left: 0,
                        right: 0,
                        zIndex: 20,
                        marginTop: 4,
                        maxHeight: 200,
                        overflowY: "auto",
                        borderRadius: 12,
                        border: styles.formInput.border,
                        background: styles.formInput.background,
                        backdropFilter: "blur(10px)",
                      }}
                    >
                      {filteredReports.map((emp) => (
                        <div
                          key={emp.infinityCode}
                          onMouseDown={() => {
                            setAssigneeCode(emp.infinityCode);
                            setShowSuggestions(false);
                          }}
                          style={{
                            padding: "10px 14px",
                            cursor: "pointer",
                            color: styles.formInput.color,
                          }}
                        >
                          {emp.name}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <div style={styles.formGrid}>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>{t.priority}</label>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {PRIORITIES.map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setPriority(p)}
                      style={{
                        ...styles.formInput,
                        width: "auto",
                        minHeight: "unset",
                        padding: "6px 14px",
                        cursor: "pointer",
                        border:
                          priority === p
                            ? `2px solid ${PRIORITY_COLORS[p]}`
                            : styles.formInput.border,
                        color: priority === p ? PRIORITY_COLORS[p] : styles.formInput.color,
                        fontWeight: priority === p ? 700 : 500,
                      }}
                    >
                      {t[PRIORITY_LABEL_KEY[p]]}
                    </button>
                  ))}
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.formLabel}>{t.dueDate}</label>
                {/* Plain, always-visible date picker — optional (an empty
                    value just means no due date), no separate "Add due date"
                    toggle step needed to reach it. */}
                <DatePicker
                  theme={theme}
                  isRTL={isRTL}
                  value={dueDate}
                  onChange={(v) => setDueDate(v)}
                  clearable
                  buttonStyle={styles.formInput}
                />
              </div>
            </div>

          {error && (
            <div style={{ color: "#ef4444", fontSize: 14, marginBottom: 12 }}>{error}</div>
          )}

          <div style={styles.formActions}>
            <button type="button" style={styles.actionButton("default")} onClick={onClose}>
              {t.cancel}
            </button>
            <button type="submit" style={styles.actionButton("primary")} disabled={submitting}>
              {isEdit ? t.saveChanges : t.createTask}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default TaskFormModal;
